import React, { useState } from 'react';
import {
  Paper,
  Typography,
  Box,
  TextField,
  Button,
  Stepper,
  Step,
  StepLabel,
  Alert,
  CircularProgress,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  Grid,
  List,
  ListItem,
  ListItemText,
  ListItemIcon
} from '@mui/material';
import {
  MergeType as MergeIcon,
  People as PeopleIcon,
  Warning as WarningIcon,
  CheckCircle as SuccessIcon,
  Person as PersonIcon,
  ArrowForward as ArrowIcon
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { familyMergeService } from '../../services/familyMergeService';
import { GROUP_TYPES } from '../../utils/defaultGroups';
import logger from '../../utils/logger';

const steps = ['Enter Family Codes', 'Preview Merge', 'Confirm & Execute'];

/**
 * Family Merge Tool (B.5)
 * Allows admins to merge two family accounts into one.
 * Accessible from the Admin Portal.
 */
const FamilyMergeTool = () => {
  const { currentUser } = useAuth();
  const [activeStep, setActiveStep] = useState(0);
  const [sourceFamilyCode, setSourceFamilyCode] = useState('');
  const [targetFamilyCode, setTargetFamilyCode] = useState('');
  const [defaultGroup, setDefaultGroup] = useState('EXTENDED_FAMILY');
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState(false);

  const currentFamilyCode = currentUser?.currentFamily || currentUser?.familyCode;

  const handlePreview = async () => {
    if (!sourceFamilyCode || !targetFamilyCode) {
      setError('Both family codes are required');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const mergePreview = await familyMergeService.previewMerge(sourceFamilyCode, targetFamilyCode, currentUser.uid);
      setPreview(mergePreview);
      setActiveStep(1);
    } catch (err) {
      logger.error('Error previewing merge:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleExecute = async () => {
    setConfirmDialog(false);
    setLoading(true);
    setError(null);

    try {
      const mergeResult = await familyMergeService.executeMerge(
        sourceFamilyCode,
        targetFamilyCode,
        currentUser.uid,
        { defaultGroup }
      );
      setResult(mergeResult);
      setActiveStep(2);
    } catch (err) {
      logger.error('Error executing merge:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRequestMerge = async () => {
    setLoading(true);
    setError(null);
    try {
      const adminName = currentUser.displayName || currentUser.email;
      await familyMergeService.requestMerge(
        sourceFamilyCode,
        targetFamilyCode,
        currentUser.uid,
        currentUser.email,
        adminName
      );
      setResult({
        isRequest: true,
        targetFamilyName: preview?.target.name,
        targetFamilyCode
      });
      setActiveStep(2);
    } catch (err) {
      logger.error('Error requesting merge:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setActiveStep(0);
    setSourceFamilyCode('');
    setTargetFamilyCode('');
    setDefaultGroup('EXTENDED_FAMILY');
    setPreview(null);
    setResult(null);
    setError(null);
  };

  return (
    <Paper sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <MergeIcon sx={{ mr: 1, fontSize: 28 }} />
        <Typography variant="h5">Family Merge Tool</Typography>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Merge two family accounts into one. The source family's members will be migrated
        to the target family, and the source family will be deactivated.
      </Typography>

      <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Step 1: Enter Family Codes */}
      {activeStep === 0 && (
        <Box>
          <Grid container spacing={3}>
            <Grid item xs={12} md={5}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle1" gutterBottom color="error.main">
                    Source Family (will be absorbed)
                  </Typography>
                  <TextField
                    fullWidth
                    label="Source Family Code"
                    value={sourceFamilyCode}
                    onChange={(e) => setSourceFamilyCode(e.target.value.toUpperCase())}
                    placeholder="e.g., SMIT0001"
                    variant="outlined"
                    sx={{ mt: 1 }}
                  />
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={2} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <ArrowIcon sx={{ fontSize: 40, color: 'text.secondary' }} />
            </Grid>

            <Grid item xs={12} md={5}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle1" gutterBottom color="primary.main">
                    Target Family (primary)
                  </Typography>
                  <TextField
                    fullWidth
                    label="Target Family Code"
                    value={targetFamilyCode}
                    onChange={(e) => setTargetFamilyCode(e.target.value.toUpperCase())}
                    placeholder={currentFamilyCode || 'e.g., JOHN0001'}
                    variant="outlined"
                    sx={{ mt: 1 }}
                    helperText={currentFamilyCode ? `Your family: ${currentFamilyCode}` : ''}
                  />
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          <Box sx={{ mt: 3 }}>
            <FormControl sx={{ minWidth: 250 }}>
              <InputLabel>Default Group for New Members</InputLabel>
              <Select
                value={defaultGroup}
                onChange={(e) => setDefaultGroup(e.target.value)}
                label="Default Group for New Members"
              >
                {Object.keys(GROUP_TYPES).map((type) => (
                  <MenuItem key={type} value={type}>
                    {GROUP_TYPES[type].name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              onClick={handlePreview}
              disabled={loading || !sourceFamilyCode || !targetFamilyCode}
            >
              {loading ? <CircularProgress size={20} sx={{ mr: 1 }} /> : null}
              Preview Merge
            </Button>
          </Box>
        </Box>
      )}

      {/* Step 2: Preview */}
      {activeStep === 1 && preview && (
        <Box>
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} md={6}>
              <Card variant="outlined" sx={{ borderColor: 'error.light' }}>
                <CardContent>
                  <Typography variant="subtitle1" color="error.main" gutterBottom>
                    Source: {preview.source.name}
                  </Typography>
                  <Typography variant="body2">
                    Members: {preview.source.memberCount} | Groups: {preview.source.groupCount}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card variant="outlined" sx={{ borderColor: 'primary.light' }}>
                <CardContent>
                  <Typography variant="subtitle1" color="primary.main" gutterBottom>
                    Target: {preview.target.name}
                  </Typography>
                  <Typography variant="body2">
                    Members: {preview.target.memberCount} | Groups: {preview.target.groupCount}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          <Divider sx={{ my: 2 }} />

          <Typography variant="h6" gutterBottom>Merge Summary</Typography>

          <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
            <Chip
              icon={<PeopleIcon />}
              label={`${preview.merge.newMembersToAdd} new members to add`}
              color="primary"
            />
            <Chip
              icon={<WarningIcon />}
              label={`${preview.merge.overlappingMembers} already in both`}
              color={preview.merge.overlappingMembers > 0 ? 'warning' : 'default'}
            />
            <Chip
              label={`${preview.merge.totalMembersAfterMerge} total after merge`}
              variant="outlined"
            />
          </Box>

          {/* New Members to Add */}
          {preview.merge.newMembers.length > 0 && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" gutterBottom>
                Members to Migrate
              </Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Name</TableCell>
                      <TableCell>Email</TableCell>
                      <TableCell>Role</TableCell>
                      <TableCell>Assigned Group</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {preview.merge.newMembers.map((member) => (
                      <TableRow key={member.id}>
                        <TableCell>{member.name}</TableCell>
                        <TableCell>{member.email}</TableCell>
                        <TableCell>{member.role}</TableCell>
                        <TableCell>
                          <Chip label={GROUP_TYPES[defaultGroup]?.name || defaultGroup} size="small" />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}

          {/* Overlapping Members */}
          {preview.merge.overlappingMemberDetails.length > 0 && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" gutterBottom>
                Members Already in Both Families (will be skipped)
              </Typography>
              <List dense>
                {preview.merge.overlappingMemberDetails.map((member) => (
                  <ListItem key={member.id}>
                    <ListItemIcon><PersonIcon /></ListItemIcon>
                    <ListItemText
                      primary={member.name}
                      secondary={member.email}
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
          )}

          <Alert severity="warning" sx={{ mb: 3 }}>
            This action cannot be easily undone. The source family ({preview.source.name})
            will be deactivated after the merge.
          </Alert>

          {!preview?.callerIsTargetAdmin && (
            <Alert severity="info" sx={{ mb: 2 }}>
              You are not an admin of the target family (<strong>{preview?.target.name}</strong>).
              Submitting will send a merge request to their admins for approval.
            </Alert>
          )}

          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Button onClick={() => setActiveStep(0)}>Back</Button>
            {preview?.callerIsTargetAdmin ? (
              <Button
                variant="contained"
                color="warning"
                onClick={() => setConfirmDialog(true)}
                disabled={loading}
              >
                {loading ? <CircularProgress size={20} sx={{ mr: 1 }} /> : null}
                Execute Merge
              </Button>
            ) : (
              <Button
                variant="contained"
                color="primary"
                onClick={handleRequestMerge}
                disabled={loading}
              >
                {loading ? <CircularProgress size={20} sx={{ mr: 1 }} /> : null}
                Request Merge
              </Button>
            )}
          </Box>
        </Box>
      )}

      {/* Step 3: Result */}
      {activeStep === 2 && result && (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <SuccessIcon sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
          {result.isRequest ? (
            <>
              <Typography variant="h5" gutterBottom>Merge Request Sent</Typography>
              <Typography variant="body1" color="text.secondary" gutterBottom>
                Your request has been sent to the admins of <strong>{result.targetFamilyName}</strong>.
                They will review and approve the merge from their Admin Portal.
              </Typography>
            </>
          ) : (
            <>
              <Typography variant="h5" gutterBottom>Merge Complete</Typography>
              <Typography variant="body1" color="text.secondary" gutterBottom>
                Successfully merged families.
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mt: 3 }}>
                <Chip label={`${result.membersMigrated} members migrated`} color="success" />
                <Chip label={`${result.membersSkipped} skipped (already present)`} />
                <Chip label={`${result.totalMembers} total members`} variant="outlined" />
              </Box>
            </>
          )}
          <Button variant="contained" sx={{ mt: 4 }} onClick={handleReset}>
            {result.isRequest ? 'Submit Another Request' : 'Merge Another Family'}
          </Button>
        </Box>
      )}

      {/* Confirmation Dialog */}
      <Dialog open={confirmDialog} onClose={() => setConfirmDialog(false)}>
        <DialogTitle>Confirm Family Merge</DialogTitle>
        <DialogContent>
          <DialogContentText>
            You are about to merge <strong>{preview?.source.name}</strong> into{' '}
            <strong>{preview?.target.name}</strong>.
          </DialogContentText>
          <DialogContentText sx={{ mt: 2, color: 'error.main' }}>
            This will:
          </DialogContentText>
          <List dense>
            <ListItem>
              <ListItemText primary={`Migrate ${preview?.merge.newMembersToAdd} members to ${preview?.target.name}`} />
            </ListItem>
            <ListItem>
              <ListItemText primary={`Deactivate ${preview?.source.name} (${sourceFamilyCode})`} />
            </ListItem>
            <ListItem>
              <ListItemText primary="Create an audit log entry for this merge" />
            </ListItem>
          </List>
          <DialogContentText sx={{ mt: 1, fontWeight: 'bold' }}>
            This action cannot be easily reversed.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialog(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleExecute}
          >
            Confirm Merge
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default FamilyMergeTool;
